import { useState } from 'react';
import {
  Callout,
  Confirm,
  Empty,
  Field,
  Loading,
  Panel,
  TableScroll,
  TextInput,
  explainFailure,
  formatCount,
  type Load,
} from '@affdice/portal-kit';
import { PlatformApi } from '../../../client.ts';
import { t } from '../../../messages.ts';

/** Volume tiers — the bands an operator's platform charge is billed by. */
export function VolumeTiersSection({
  api,
  operatorId,
  canWrite,
  terms,
  form,
  refresh,
  onChanged,
}: {
  api: PlatformApi;
  operatorId: string;
  canWrite: boolean;
  terms: Load<Awaited<ReturnType<PlatformApi['chargeTerms']>>>;
  /** The band form's draft, held by the detail popup for the same reason as Go-live's country. */
  form: {
    fromUnits: string;
    setFromUnits: (v: string) => void;
    toUnits: string;
    setToUnits: (v: string) => void;
    rateBps: string;
    setRateBps: (v: string) => void;
    actionError: unknown;
    setActionError: (e: unknown) => void;
  };
  refresh: () => void;
  onChanged?: (() => void) | undefined;
}) {
  const { fromUnits, setFromUnits, toUnits, setToUnits, rateBps, setRateBps, actionError, setActionError } = form;
  // Item 6 (third batch) — which band's Delete is being confirmed, keyed by
  // its own from_units (the band's key, and the only handle it has).
  const [deletingBand, setDeletingBand] = useState<string | null>(null);
  const termId = terms.state === 'ready' ? terms.value.items[0]?.id : undefined;
  return (
    <Panel title={t('ops.tiers.title')}>
      {terms.state !== 'ready' ? (
        <Loading rows={1} />
      ) : !termId ? (
        <Empty>{t('ops.tiers.noTerm')}</Empty>
      ) : (
        <>
          <Callout>
            {t('ops.tiers.termLine', { id: termId.slice(-6), model: terms.value.items[0]?.model ?? '' })}
          </Callout>
          {terms.value.tiers.length === 0 ? (
            <Empty>{t('ops.tiers.noBands')}</Empty>
          ) : (
            <TableScroll>
              <table>
                <thead>
                  <tr>
                    <th>{t('ops.tiers.from')}</th><th>{t('ops.tiers.to')}</th><th className="num">{t('ops.tiers.rate')}</th>
                    <th className="num">{t('ops.tiers.flat')}</th><th className="col-action" />
                  </tr>
                </thead>
                <tbody>
                  {terms.value.tiers.map((tier) => (
                    <tr key={`${tier.charge_term_id}-${tier.from_units}`}>
                      <td className="num">{formatCount(Number(tier.from_units))}</td>
                      <td className="num">{tier.to_units == null ? '∞' : formatCount(Number(tier.to_units))}</td>
                      <td className="num">{tier.rate_bps ?? '—'}</td>
                      <td className="num">{tier.flat_minor ?? '—'}</td>
                      {/* Item 6 (third batch) — a band could be added and never
                          corrected or removed. Edit loads it into the form
                          below, which upserts on the same from_units.

                          One action cell, not two. Two adjacent columns
                          cannot both be pinned to the right edge — they
                          would stack on the same spot — and this row's two
                          buttons are one set of actions on one band, so they
                          travel together in the cell that stays put while
                          the rest of the table scrolls. */}
                      <td className="num col-action">
                        {/* Batch 2 item 1 — read-only staff never see a
                            write control that can only refuse; the actions
                            cell is empty for them rather than disabled. */}
                        {canWrite ? (
                          <div className="row">
                            <button
                              className="copy"
                              onClick={() => {
                                setFromUnits(String(tier.from_units));
                                setToUnits(tier.to_units == null ? '' : String(tier.to_units));
                                setRateBps(String(tier.rate_bps ?? 0));
                              }}
                            >
                              {t('common.edit')}
                            </button>
                            {/* Batch 2 item 2 — a destructive row trigger. */}
                            <button
                              className="link-button danger"
                              onClick={() => setDeletingBand(String(tier.from_units))}
                            >
                              {t('common.delete')}
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
          {canWrite ? (
            <>
              {actionError ? <Callout tone="error">{explainFailure(actionError).message}</Callout> : null}
              <div className="form-row">
                <Field label={t('ops.tiers.fromUnits')}>
                  <TextInput value={fromUnits} onChange={setFromUnits} />
                </Field>
                <Field label={t('ops.tiers.toUnits')}>
                  <TextInput value={toUnits} onChange={setToUnits} />
                </Field>
                <Field label={t('ops.tiers.rate')}>
                  <TextInput value={rateBps} onChange={setRateBps} />
                </Field>
                <button
                  className="copy"
                  disabled={!termId}
                  onClick={() => {
                    setActionError(null);
                    void api.putChargeTier(operatorId, termId!, {
                      from_units: fromUnits,
                      ...(toUnits.trim() !== '' ? { to_units: toUnits } : { to_units: null }),
                      rate_bps: Number(rateBps),
                    }).then(() => { refresh(); onChanged?.(); })
                      .catch((e: unknown) => setActionError(e));
                  }}
                >
                  {t('ops.tiers.saveBand')}
                </button>
              </div>
            </>
          ) : null}

          {deletingBand !== null ? (
            <Confirm
              title={t('ops.tiers.deleteTitle', { from: deletingBand })}
              tone="error"
              confirmLabel={t('common.delete')}
              body={t('ops.tiers.deleteBody')}
              onCancel={() => setDeletingBand(null)}
              onConfirm={async () => {
                await api.deleteChargeTier(operatorId, termId!, deletingBand);
                setDeletingBand(null);
                refresh();
                onChanged?.();
              }}
            />
          ) : null}
        </>
      )}
    </Panel>
  );
}
